// ── Match tracking for the pro-shop Course Scroll Map ─────────────────────────
// Turns the raw tournaments/*/tracking writes into ONE dot per MATCH, with:
//   • one dot per match (freshest of the match's two teams — one broadcaster is
//     enough; if both broadcast, the freshest fix wins, never two dots)
//   • forward-only zones (tee → fairway → green; never backward within a hole)
//   • one-green-one-match ownership (the first match to enter a green owns it; a
//     trailing match on the same green is held at FAIRWAY until the owner leaves)
//   • accelerator (a match that scores its current hole is advanced off the green
//     toward the next tee immediately, freeing the green)
//   • forget-proof (with no score, the forward-only + detectHole logic still moves
//     a match off the green when its GPS reaches the next hole)
//
// Pure + stateless-except-for-`state`: callers pass a persistent `state` object
// (kept in a ref) so ownership / forward zones / freshness survive across renders.
import { detectHole, geofenceZone, advanceZone, zoneName, zonesForHole, ZONES } from './prodMapPoints.js'

// Find a tracking team inside a tournament's flights → its match partner.
function findFlightTeam(tourn, teamId) {
  const flights = tourn?.flights || {}
  for (const [flightId, f] of Object.entries(flights)) {
    if (flightId.startsWith('_')) continue
    const team = f?.teams?.[teamId]
    if (team) return { flightId, opponentId: team?.info?.opponentId || null, name: team?.info?.name || null }
  }
  return null
}

// Canonical, stable key for a match (paired teams share one key; solo = its own).
function matchKeyFor(tid, teamId, fi) {
  if (fi && fi.opponentId) {
    const pair = [teamId, fi.opponentId].sort()
    return `${tid}|${fi.flightId}|${pair[0]}~${pair[1]}`
  }
  return `${tid}|solo|${teamId}`
}

// Does any team in this match have a posted score at the given hole? (match play:
// flights/{f}/teams/{team}/scores/{hole}.bestBall). Drives the accelerator.
function matchScoredHole(tourn, m, hole) {
  if (!tourn || hole == null) return false
  const flights = tourn.flights || {}
  for (const teamId of m.teamIds) {
    for (const [flightId, f] of Object.entries(flights)) {
      if (flightId.startsWith('_')) continue
      const sc = f?.teams?.[teamId]?.scores?.[hole]
      if (sc && Number.isFinite(sc.bestBall) && sc.bestBall > 0) return true
    }
  }
  return false
}

const uniq = (arr) => Array.from(new Set(arr.filter(Boolean)))

// Compute the display dots. `state` (mutated in place) holds:
//   zone:{[key]:{hole,zoneIdx}}  greenOwner:{[hole]:key}  cleared:{[key]:hole}
//   fresh:{[key]:{lastUa,receivedAt}}
export function computeMatchDots(all, spots, state, nowLocal, staleMs) {
  state.zone ||= {}; state.greenOwner ||= {}; state.cleared ||= {}; state.fresh ||= {}

  // 1) Group every valid tracking fix into its match; keep the freshest fix.
  const matches = {}
  for (const [tid, tourn] of Object.entries(all || {})) {
    if (!tourn || tourn?.info?.status === 'closed') continue
    for (const [teamId, rec] of Object.entries(tourn.tracking || {})) {
      if (!rec || !Number.isFinite(rec.lat) || !Number.isFinite(rec.lng)) continue
      const fi = findFlightTeam(tourn, teamId)
      const key = matchKeyFor(tid, teamId, fi)
      const ua = Number.isFinite(rec.updatedAt) ? rec.updatedAt : 0
      const m = matches[key] || (matches[key] = { key, tid, tourn, teamIds: [], names: [], fix: null })
      m.teamIds.push(teamId)
      m.names.push(rec.teamName || fi?.name || null)
      if (!m.fix || ua > m.fix.updatedAt) m.fix = { lat: rec.lat, lng: rec.lng, updatedAt: ua }
    }
  }

  // 2) Freshness on OUR clock (per match) → active set + raw hole.
  const active = []
  for (const m of Object.values(matches)) {
    const f = state.fresh[m.key] || (state.fresh[m.key] = { lastUa: -1, receivedAt: 0 })
    if (m.fix.updatedAt !== f.lastUa) { f.lastUa = m.fix.updatedAt; f.receivedAt = nowLocal }
    if (nowLocal - f.receivedAt > staleMs) continue
    m.receivedAt = f.receivedAt
    m.rawHole = detectHole(m.fix.lat, m.fix.lng)
    active.push(m)
  }
  const activeKeys = new Set(active.map((m) => m.key))

  // 3) Forward-only zone advance + accelerator (score → leave the green).
  for (const m of active) {
    const adv = advanceZone(state.zone[m.key], m.rawHole, m.fix.lat, m.fix.lng)
    state.zone[m.key] = adv
    let hole = adv.hole
    let zone = zoneName(adv)
    // Accelerator: scored the hole they're on → mark it cleared (leaving).
    if (m.rawHole != null && zone === 'green' && matchScoredHole(m.tourn, m, m.rawHole)) {
      state.cleared[m.key] = m.rawHole
    }
    const cl = state.cleared[m.key]
    if (cl != null) {
      if (m.rawHole != null && m.rawHole > cl) delete state.cleared[m.key]  // GPS passed it → resume
      else { hole = Math.min(18, cl + 1); zone = 'tee' }                     // pinned to next tee
    }
    m.hole = hole; m.zone = zone
  }

  // 4) One-green-one-match ownership. Release greens whose owner left / went stale;
  //    then first active match on a green claims it, later matches wait at fairway.
  for (const h of Object.keys(state.greenOwner)) {
    const owner = state.greenOwner[h]
    const om = active.find((m) => m.key === owner)
    if (!om || !activeKeys.has(owner) || !(om.zone === 'green' && String(om.hole) === String(h))) {
      delete state.greenOwner[h]
    }
  }
  active.sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0)) // deterministic claim order
  for (const m of active) {
    if (m.zone !== 'green') continue
    const h = String(m.hole)
    const owner = state.greenOwner[h]
    if (!owner) state.greenOwner[h] = m.key       // first claim
    else if (owner !== m.key) {
      // Held behind → show at the last forward zone BEFORE the green: fairway on
      // a par 4/5, tee on a par 3 (no fairway zone/spot).
      const zs = zonesForHole(m.hole)
      m.zone = zs[zs.length - 2] || 'tee'
    }
  }

  // 5) Build dots (one per match) — only where a spot is placed.
  const dots = []
  for (const m of active) {
    const spot = spots?.[m.hole]?.[m.zone]
    if (!spot || !Number.isFinite(spot.x) || !Number.isFinite(spot.y)) continue
    dots.push({ key: m.key, hole: m.hole, zone: m.zone, x: spot.x, y: spot.y, names: uniq(m.names), receivedAt: m.receivedAt })
  }
  return dots
}

export { ZONES }
